import React, { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { format } from "date-fns";

import { Avatar, Button } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import HeadsetIcon from "@material-ui/icons/HeadsetMic";
import ScheduleIcon from "@material-ui/icons/Schedule";
import SwapHorizIcon from "@material-ui/icons/SwapHoriz";
import SmartToyIcon from "@material-ui/icons/Android";
import BoltIcon from "@material-ui/icons/FlashOn";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import { AEROS, contrastTextFor, mixHex } from "../../styles/aerosTokens";
import ButtonWithSpinner from "../../components/ButtonWithSpinner";
import ScheduleModal from "../../components/ScheduleModal";
import TransferTicketModalCustom from "../../components/TransferTicketModalCustom";
import { useEntryStyles } from "./entryStyles";
import {
  customerSince,
  formatWait,
  initials,
  lastContactLabel,
} from "./entryRules";

/**
 * Cartão que ocupa o painel de conversa quando o atendente seleciona um ticket
 * que ainda está aguardando: quem é o contato, há quanto tempo espera, o que
 * pediu, e os três caminhos (transferir, agendar, assumir).
 *
 * Só aparece para ticket `pending`. Assim que ele é assumido, o painel volta a
 * ser a conversa — o cartão é uma antessala, não uma tela paralela.
 */
const TicketContextCard = ({ ticket, contact, onAccepted }) => {
  const classes = useEntryStyles();
  const theme = useTheme();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const { setCurrentTicket, setTabOpen } = useContext(TicketsContext);

  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);

  useEffect(() => {
    if (!ticket?.id) return;
    let vivo = true;
    (async () => {
      try {
        const { data } = await api.get(`/tickets/${ticket.id}/context`);
        if (vivo) setCtx(data);
      } catch (err) {
        // O cartão continua útil sem os extras (nome, telefone, tags e a
        // última mensagem vêm do próprio ticket), então a falha aqui não
        // vira toast: só some com os blocos que dependem dela.
        if (vivo) setCtx(null);
      }
    })();
    return () => {
      vivo = false;
    };
  }, [ticket?.id]);

  /**
   * Assumir o atendimento. Mesma regra do botão "Aceitar" do cabeçalho da
   * conversa (`TicketActionButtonsCustom`): grava status/userId e, quando a
   * empresa tem `sendGreetingAccepted` ligada, o backend dispara a saudação.
   *
   * Ticket sem fila NÃO é assumido daqui: o fluxo antigo abre um modal para
   * escolher a fila antes, e pular essa etapa deixaria o ticket sem fila
   * definida. Nesse caso o cartão manda o atendente para a conversa, onde o
   * botão com o modal já existe.
   */
  const assumir = async () => {
    if (!ticket?.queueId) {
      history.push(`/tickets/${ticket.uuid}`);
      onAccepted?.();
      return;
    }

    setLoading(true);
    try {
      await api.put(`/tickets/${ticket.id}`, {
        status: "open",
        userId: user?.id,
      });
      setTabOpen("open");
      setCurrentTicket({ ...ticket, code: "#open" });
      onAccepted?.();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const espera = formatWait(ctx?.waitSeconds);
  const desde = customerSince(ctx?.contact?.createdAt);
  const atendimentos = ctx?.contact?.ticketCount
    ? i18n.t("aeros.ticketsEntry.context.tickets", {
        count: ctx.contact.ticketCount,
      })
    : null;
  const ultimoContato =
    lastContactLabel(ctx?.contact?.lastTicketAt) ??
    (ctx ? i18n.t("aeros.ticketsEntry.context.firstContact") : null);

  const meta = [contact?.number, desde, atendimentos].filter(Boolean).join(" · ");

  const avatarBg =
    AEROS.avatarCycle[(contact?.id || 0) % AEROS.avatarCycle.length];

  const corDaFila = ticket?.queue?.color || AEROS.cyan;

  // Os três blocos de baixo vêm dos campos personalizados do contato — é o que
  // existe de genérico para o "Plano / Fatura" do mockup, que ali eram dados de
  // ERP. Contato sem campo personalizado não mostra a grade.
  const tiles = (contact?.extraInfo || []).slice(0, 3);

  return (
    <div className={classes.contextRoot}>
      {transferOpen && (
        <TransferTicketModalCustom
          modalOpen={transferOpen}
          onClose={() => setTransferOpen(false)}
          ticketid={ticket.id}
          ticket={ticket}
        />
      )}
      {scheduleOpen && (
        <ScheduleModal
          open={scheduleOpen}
          onClose={() => setScheduleOpen(false)}
          aria-labelledby="form-dialog-title"
          contactId={contact?.id}
        />
      )}

      <div className={classes.breadcrumb}>
        <ArrowBackIcon
          className={classes.breadcrumbBack}
          style={{ fontSize: 18, cursor: "pointer" }}
          onClick={() => history.push("/tickets")}
        />
        <Button
          className={classes.breadcrumbLink}
          onClick={() => history.push("/tickets")}
        >
          {i18n.t("aeros.ticketsEntry.context.back")}
        </Button>
        <ChevronRightIcon
          style={{ fontSize: 16, color: theme.palette.aeros.textTertiary }}
        />
        <span className={classes.breadcrumbCurrent}>
          {contact?.name}{" "}
          <span className={classes.breadcrumbId}>#{ticket?.id}</span>
        </span>
        <span
          className={classes.chip}
          style={{
            color: AEROS.warning,
            background: "rgba(251,191,36,0.12)",
            fontSize: 10.5,
            fontWeight: 700,
            padding: "2px 8px",
          }}
        >
          {i18n.t("ticketsList.pendingHeader")}
        </span>
      </div>

      <div className={classes.contextBody}>
        <div className={classes.contextCard}>
          <div className={classes.contextHead}>
            <Avatar
              src={contact?.urlPicture || contact?.profilePicUrl}
              className={classes.contextAvatar}
              style={{ background: avatarBg, color: contrastTextFor(avatarBg) }}
            >
              {initials(contact?.name)}
            </Avatar>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className={classes.contextName}>{contact?.name}</div>
              {meta && <div className={classes.contextMeta}>{meta}</div>}
            </div>
            {espera && (
              <div className={classes.contextWait}>
                <div
                  className={classes.contextWaitValue}
                  style={{
                    color:
                      (ctx?.waitSeconds ?? 0) >= 600
                        ? AEROS.error
                        : theme.palette.text.primary,
                  }}
                >
                  {espera}
                </div>
                <div className={classes.contextWaitLabel}>
                  {i18n.t("aeros.ticketsEntry.context.waiting")}
                </div>
              </div>
            )}
          </div>

          <div className={classes.contextMain}>
            <div className={classes.chipRow}>
              {ticket?.queue?.name && (
                <span
                  className={classes.chip}
                  style={{
                    color: corDaFila,
                    background: `${corDaFila}1F`,
                  }}
                >
                  <span
                    className={classes.chipDot}
                    style={{ background: corDaFila }}
                  />
                  {ticket.queue.name}
                </span>
              )}
              {(contact?.tags || []).slice(0, 4).map((tag) => {
                const cor = tag.color || AEROS.cyan;
                return (
                  <span
                    key={tag.id}
                    className={classes.chip}
                    style={{ color: cor, background: `${cor}1F` }}
                  >
                    {tag.name}
                  </span>
                );
              })}
              {ctx?.passouPeloChatbot && (
                <span
                  className={classes.chip}
                  style={{
                    color: mixHex(theme.palette.primary.main, "#FFFFFF", 0.3),
                    background: "rgba(160,68,255,0.14)",
                  }}
                >
                  <SmartToyIcon style={{ fontSize: 14 }} />
                  {i18n.t("aeros.ticketsEntry.context.viaChatbot")}
                </span>
              )}
            </div>

            <div className={classes.lastMessageBox}>
              <div className={classes.lastMessageLabel}>
                {i18n.t("aeros.ticketsEntry.context.lastMessage")}
              </div>
              <div className={classes.lastMessageText}>
                {ticket?.lastMessage
                  ? `“${ticket.lastMessage}”`
                  : i18n.t("aeros.ticketsEntry.context.noMessage")}
              </div>
              {ticket?.updatedAt && (
                <div className={classes.lastMessageFoot}>
                  {i18n.t("aeros.ticketsEntry.context.receivedAt", {
                    time: format(new Date(ticket.updatedAt), "HH:mm"),
                    channel: ticket?.whatsapp?.name || ticket?.channel || "—",
                  })}
                </div>
              )}
            </div>

            {(tiles.length > 0 || ultimoContato) && (
              <div className={classes.contextGrid}>
                {tiles.map((info) => (
                  <div className={classes.contextTile} key={info.id ?? info.name}>
                    <div className={classes.contextTileLabel}>{info.name}</div>
                    <div className={classes.contextTileValue}>{info.value}</div>
                  </div>
                ))}
                {ultimoContato && (
                  <div className={classes.contextTile}>
                    <div className={classes.contextTileLabel}>
                      {i18n.t("aeros.ticketsEntry.context.lastContact")}
                    </div>
                    <div className={classes.contextTileValue}>{ultimoContato}</div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className={classes.contextFoot}>
            <Button
              className={classes.contextGhost}
              startIcon={<SwapHorizIcon style={{ fontSize: 18 }} />}
              onClick={() => setTransferOpen(true)}
            >
              {i18n.t("aeros.ticketsEntry.context.transfer")}
            </Button>
            <Button
              className={classes.contextGhost}
              startIcon={<ScheduleIcon style={{ fontSize: 18 }} />}
              onClick={() => setScheduleOpen(true)}
            >
              {i18n.t("aeros.ticketsEntry.context.schedule")}
            </Button>
            <ButtonWithSpinner
              loading={loading}
              className={classes.contextAccept}
              startIcon={<HeadsetIcon style={{ fontSize: 19 }} />}
              onClick={assumir}
            >
              {i18n.t("aeros.ticketsEntry.context.accept")}
            </ButtonWithSpinner>
          </div>
        </div>

        <div className={classes.contextHint}>
          <BoltIcon style={{ fontSize: 15 }} />
          {i18n.t("aeros.ticketsEntry.context.acceptHint")}
        </div>
      </div>
    </div>
  );
};

export default TicketContextCard;
